// Evidence of a rebill — what the carrier charged / measured — shared by the
// Carrier Adjustments drawer and the Disputes drawer so the two can never show
// different things (Sahin 2026-10-11). Sections render only when the rebill's
// evidence_json carries that carrier's data:
//   * Carrier Charges — DHL extra charges, FedEx/UPS charge lines, UniUni fees
//   * USPS Census     — manifested vs assessed weight, dims, zone, class, postage
//   * Reason          — the rebill_reason text
// Internal screens only (the census postage is our cost) — never a carrier file.
export function RebillEvidence({ evidence, carrier, reason }: {
  evidence: unknown
  carrier: string | null | undefined
  reason: string | null | undefined
}) {
  return (
    <>
      {(() => {
        const ev = evidence as Record<string, unknown> | null
        if (!ev) return null

        // DHL xc_charges / dhl_xc_breakdown
        type XcLine = { code: string | null; name: string | null; charge: string | null }
        const xcRaw = (ev.xc_charges ?? ev.dhl_xc_breakdown) as XcLine[] | null
        const xcLines = Array.isArray(xcRaw)
          ? xcRaw.filter(x => x && x.charge != null && parseFloat(String(x.charge)) !== 0)
          : []

        // UniUni fee breakdown
        const uurFields: [string, string][] = [
          ['Net Price', 'net_price'], ['Fuel Surcharge', 'fuel_surcharge'],
          ['GST', 'gst'], ['QST', 'qst'], ['UU Tax', 'uu_tax'],
        ]
        const uurLines = uurFields.filter(([, k]) => ev[k] != null && Number(ev[k]) !== 0)

        // FedEx / UPS per-invoice charge lines (same shape)
        type FxLine = { description: string | null; amount: string | null }
        const fxRaw = (ev.fedex_charges ?? ev.ups_charges) as FxLine[] | null
        const fxLines = Array.isArray(fxRaw)
          ? fxRaw.filter(x => x && x.description && x.amount != null && parseFloat(String(x.amount)) !== 0)
          : []

        if (xcLines.length === 0 && uurLines.length === 0 && fxLines.length === 0) return null
        return (
          <div>
            <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">Carrier Charges</div>
            <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
              <tbody>
                {xcLines.map((x, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f4f4f5' }}>
                    <td style={{ padding: '4px 0', color: '#71717a', whiteSpace: 'nowrap' }}>{x.code || ''}</td>
                    <td style={{ padding: '4px 8px', color: '#18181b' }}>{x.name || ''}</td>
                    <td style={{ padding: '4px 0', textAlign: 'right', fontWeight: 500, color: '#18181b', whiteSpace: 'nowrap' }}>
                      ${parseFloat(String(x.charge)).toFixed(2)}
                    </td>
                  </tr>
                ))}
                {fxLines.map((x, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f4f4f5' }}>
                    <td colSpan={2} style={{ padding: '4px 8px', color: '#18181b' }}>{x.description}</td>
                    <td style={{ padding: '4px 0', textAlign: 'right', fontWeight: 500, color: '#18181b', whiteSpace: 'nowrap' }}>
                      ${parseFloat(String(x.amount)).toFixed(2)}
                    </td>
                  </tr>
                ))}
                {uurLines.map(([label, key]) => (
                  <tr key={key} style={{ borderBottom: '1px solid #f4f4f5' }}>
                    <td style={{ padding: '4px 0', color: '#71717a', whiteSpace: 'nowrap' }}>{label}</td>
                    <td style={{ padding: '4px 8px' }}></td>
                    <td style={{ padding: '4px 0', textAlign: 'right', fontWeight: 500, color: '#18181b', whiteSpace: 'nowrap' }}>
                      ${Number(ev[key]).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      })()}
      {(() => {
        // USPS census evidence: what was manifested vs what USPS assessed.
        const ev = evidence as Record<string, unknown> | null
        if (!ev || String(ev.carrier ?? carrier).toUpperCase() !== 'USPS') return null
        const num = (v: unknown) => (v == null || v === '' || Number.isNaN(Number(v)) ? null : Number(v))
        const lbs = (v: unknown) => { const n = num(v); return n == null ? '—' : `${n.toFixed(2)} lbs` }
        const usd = (v: unknown) => { const n = num(v); return n == null ? '—' : `$${n.toFixed(2)}` }
        const dims = (v: unknown) => {
          if (!Array.isArray(v) || v.length !== 3) return '—'
          const n = v.map(num)
          return n.some(x => x == null) ? '—' : `${n.map(x => (x as number).toFixed(1)).join(' × ')} in`
        }
        const txt = (v: unknown) => (v == null || v === '' ? '—' : String(v))
        const hasCensus = ['manifest_weight_lbs', 'assessed_weight_lbs', 'paid_postage', 'assessed_postage']
          .some(k => num(ev[k]) != null)
        if (!hasCensus) {
          return (
            <div>
              <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">USPS Census</div>
              <p className="text-[11px] text-honey-700 bg-honey-50 rounded-md p-2">No census record attached to this rebill.</p>
            </div>
          )
        }
        const rows: [string, string, string][] = [
          ['Weight',        lbs(ev.manifest_weight_lbs), lbs(ev.assessed_weight_lbs)],
          ['Dim. weight',   '—',                         lbs(ev.dimensional_weight_lbs)],
          ['Dimensions',    dims(ev.manifest_dims_in),   dims(ev.assessed_dims_in)],
          ['Zone',          txt(ev.manifested_zone),     txt(ev.assessed_zone)],
          ['Mail class',    txt(ev.manifest_mail_class), txt(ev.assessed_mail_class)],
          ['Postage',       usd(ev.paid_postage),        usd(ev.assessed_postage)],
        ]
        return (
          <div>
            <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">USPS Census</div>
            <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: '#a1a1aa' }}>
                  <th style={{ textAlign: 'left', fontWeight: 500, padding: '2px 0' }}></th>
                  <th style={{ textAlign: 'right', fontWeight: 500, padding: '2px 8px' }}>Manifested</th>
                  <th style={{ textAlign: 'right', fontWeight: 500, padding: '2px 0' }}>Assessed by USPS</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([label, m, a]) => (
                  <tr key={label} style={{ borderBottom: '1px solid #f4f4f5' }}>
                    <td style={{ padding: '4px 0', color: '#71717a', whiteSpace: 'nowrap' }}>{label}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', color: '#18181b', whiteSpace: 'nowrap' }}>{m}</td>
                    <td style={{ padding: '4px 0', textAlign: 'right', fontWeight: 500, color: m !== a && a !== '—' && m !== '—' ? '#b45309' : '#18181b', whiteSpace: 'nowrap' }}>{a}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      })()}
      {reason && (
        <div>
          <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">Reason</div>
          <p className="text-[11px] text-ink-700 whitespace-pre-wrap bg-ink-50 rounded-md p-3">{reason}</p>
        </div>
      )}
    </>
  )
}
